import { FC, useRef, useState, DragEvent, useEffect } from 'react'
import { useSnackbar } from 'notistack'

import {
  Button,
  IconButton,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Fade,
  Chip,
} from '@mui/material'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import CloseIcon from '@mui/icons-material/Close'

// Style
import './ImportSchedule.scss'

// Logic hook
import useImportScheduleLogic from './ImportSchedule.logic'

// Components
import { ImportSummaryContent } from './components'

// Types
import { ImportScheduleProps, ImportSummaryData } from './types'

const ImportSchedule: FC<ImportScheduleProps> = (props) => {
  const { open, onClose } = props
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const {
    selectedFile,
    isConfirmDialogOpen,
    isSummaryDialogOpen,
    importSummary,
    isUploading,
    uploadError,
    snackbar,
    handleFileChange,
    handleConfirmUpload,
    handleCloseConfirmDialog,
    handleCloseSummaryDialog,
    handleCloseSnackbar,
  } = useImportScheduleLogic(props)

  // Display error notifications using notistack
  useEffect(() => {
    if (snackbar.open && snackbar.severity !== 'success') {
      enqueueSnackbar(snackbar.message, {
        variant: snackbar.severity,
        onClose: handleCloseSnackbar,
      })
      // Reset snackbar state in the logic to prevent showing again
      handleCloseSnackbar()
    }
  }, [
    snackbar.open,
    snackbar.message,
    snackbar.severity,
    enqueueSnackbar,
    handleCloseSnackbar,
  ])

  // Handle file chip delete (clear file selection)
  const handleClearFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    // Call handleFileChange with a custom event that mimics an empty file selection
    const customEvent = {
      target: { files: null },
    } as React.ChangeEvent<HTMLInputElement>
    handleFileChange(customEvent)
  }

  // Drag and Drop handlers
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDragging) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Create a synthetic event to reuse handleFileChange
      const fileList = e.dataTransfer.files
      const syntheticEvent = {
        target: {
          files: fileList,
        },
      } as React.ChangeEvent<HTMLInputElement>

      handleFileChange(syntheticEvent)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h4">Import Surgery Schedule</Typography>
            <IconButton onClick={onClose} disabled={isUploading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            className={`drag-drop-area ${isDragging ? 'dragging' : ''}`}
            sx={{
              border: '2px dashed #ccc',
              borderRadius: '8px',
              padding: '30px',
              textAlign: 'center',
              marginTop: '20px',
              backgroundColor: '#f9f9f9',
              position: 'relative',
            }}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <Fade in={isUploading}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 1,
                    borderRadius: '8px',
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Processing file...
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            ) : null}
            <FileUploadIcon
              sx={{ fontSize: '48px', color: '#aaa', marginBottom: '10px' }}
            />
            <Typography variant="body1" sx={{ marginBottom: '10px' }}>
              Drag & Drop Excel File Here
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Supported formats: .xlsx, .xls, .csv
            </Typography>
            <Button
              variant="contained"
              size="medium"
              component="label"
              sx={{ marginTop: '20px' }}
              disabled={isUploading}
            >
              Browse Files
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx, .xls, .csv"
                style={{ display: 'none' }}
                id="drag-drop-input"
                onChange={handleFileChange}
                disabled={isUploading}
              />
            </Button>
          </Box>
          {selectedFile && (
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Chip
                label={selectedFile.name}
                color="primary"
                variant="outlined"
                onDelete={!isUploading ? handleClearFile : undefined}
                sx={{ maxWidth: '100%', overflow: 'hidden' }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Import Dialog */}
      <Dialog
        open={isConfirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '8px' },
        }}
      >
        <DialogTitle>
          <Typography variant="h4">Confirm Import</Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ marginBottom: '20px' }}>
            Importing this file will replace all existing surgeries for the
            dates included in the file. For example, if the file contains
            surgeries for specific days, all current surgeries on those days
            will be removed and replaced with the ones from this file. Are you
            sure you want to proceed?
          </Alert>
          {selectedFile && (
            <Typography variant="body2">
              File to import: {selectedFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleCloseConfirmDialog}
            color="primary"
            variant="outlined"
            size="medium"
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpload}
            color="primary"
            variant="contained"
            size="medium"
            disabled={isUploading}
            startIcon={
              isUploading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isUploading ? 'Uploading...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Summary Dialog */}
      <Dialog
        open={isSummaryDialogOpen}
        onClose={handleCloseSummaryDialog}
        maxWidth="lg"
        fullWidth
        className="import-summary-dialog"
      >
        <DialogTitle
          className="dialog-title"
          sx={{
            backgroundColor: '#5035ED',
            color: 'white',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
              Import Summary
            </Typography>
            <IconButton
              onClick={handleCloseSummaryDialog}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className="dialog-content">
          <ImportSummaryContent
            importSummary={importSummary as ImportSummaryData | null}
            uploadError={uploadError}
          />
        </DialogContent>
        <DialogActions className="dialog-actions">
          <Button
            onClick={handleCloseSummaryDialog}
            color="primary"
            variant="contained"
          >
            DONE
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ImportSchedule
